import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, MagnifyingGlass, ShieldCheck, DownloadSimple,
  Eye, EnvelopeSimple, Spinner, X, Check, Clock, Warning
} from '@phosphor-icons/react';

const AdminNDARegister = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [showResend, setShowResend] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/home');
      return;
    }
    fetchAgreements();
  }, [user, navigate]);

  const fetchAgreements = async (q = '') => {
    setLoading(true);
    try {
      const params = q ? `?search=${encodeURIComponent(q)}` : '';
      const { data } = await axios.get(`${API}/agreements/admin/register${params}`);
      setAgreements(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAgreements(search);
  };

  const viewDetail = async (id) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/agreements/admin/${id}`);
      setDetail(data);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const resendCopy = async () => {
    if (!selectedId) return;
    setResending(true);
    try {
      await axios.post(`${API}/agreements/admin/resend`, {
        agreement_id: selectedId,
        email: resendEmail || undefined,
      });
      setShowResend(false);
      setResendEmail('');
    } catch (err) {
      console.error(err);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-nda-register">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/invites')} className="text-slate-500 hover:text-slate-700" data-testid="admin-back-btn">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <ShieldCheck size={22} className="text-[#235347]" />
              <h1 className="font-semibold text-slate-900">NDA Register</h1>
            </div>
          </div>
          <span className="text-xs text-slate-400">{agreements.length} agreement{agreements.length !== 1 ? 's' : ''}</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or company..."
              className="w-full bg-white border border-slate-200 rounded-xl pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20 text-sm"
              data-testid="nda-search-input"
            />
          </div>
        </form>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={28} className="animate-spin text-[#235347]" />
          </div>
        ) : agreements.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <ShieldCheck size={40} className="mx-auto text-slate-300 mb-3" />
            <p>No signed agreements found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {agreements.map(a => (
              <button
                key={a.id}
                onClick={() => viewDetail(a.id)}
                className={`w-full text-left bg-white border rounded-xl p-4 hover:bg-slate-50 transition-colors ${
                  selectedId === a.id ? 'border-[#235347] ring-1 ring-[#235347]/20' : 'border-slate-200'
                }`}
                data-testid={`agreement-row-${a.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{a.signer_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{a.signer_email}</p>
                    {a.signer_company && <p className="text-xs text-slate-400">{a.signer_company} {a.signer_role ? `\u00B7 ${a.signer_role}` : ''}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{new Date(a.signed_at).toLocaleDateString()}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'executed' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                        {a.status}
                      </span>
                      <span className="text-xs text-slate-400">v{a.agreement_version}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {a.email_sent_to_signer && <EnvelopeSimple size={12} className="text-green-500" title="Email sent to signer" />}
                      {a.email_sent_to_admin && <EnvelopeSimple size={12} className="text-blue-500" title="Email sent to admin" />}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail Panel */}
      <AnimatePresence>
        {selectedId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center"
            onClick={() => { setSelectedId(null); setDetail(null); }}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl max-h-[85vh] overflow-auto p-6"
              data-testid="agreement-detail-modal"
            >
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size={24} className="animate-spin text-[#235347]" />
                </div>
              ) : detail ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-semibold text-slate-900">Agreement Detail</h2>
                    <button onClick={() => { setSelectedId(null); setDetail(null); }} className="text-slate-400">
                      <X size={24} />
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    <Row label="Agreement ID" value={detail.id} mono />
                    <Row label="Signer" value={detail.signer_name} />
                    <Row label="Email" value={detail.signer_email} />
                    {detail.signer_company && <Row label="Company" value={detail.signer_company} />}
                    {detail.signer_role && <Row label="Role" value={detail.signer_role} />}
                    <Row label="Country" value={detail.signer_country} />
                    {detail.signer_phone && <Row label="Phone" value={detail.signer_phone} />}
                    <Row label="Signed at" value={new Date(detail.signed_at).toLocaleString()} />
                    <Row label="Version" value={detail.agreement_version} />
                    <Row label="Hash" value={detail.snapshot_hash} mono />
                    <Row label="IP" value={detail.ip_address} />
                    <Row label="Status" value={detail.status} />
                    <Row label="Email to signer" value={detail.email_sent_to_signer ? 'Sent' : 'Not sent'} />
                    <Row label="Email to admin" value={detail.email_sent_to_admin ? 'Sent' : 'Not sent'} />
                  </div>

                  {/* Drawn Signature */}
                  {detail.drawn_signature && (
                    <div className="mb-6">
                      <p className="text-xs font-medium text-slate-500 mb-2">Drawn Signature</p>
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                        <img src={`data:image/png;base64,${detail.drawn_signature}`} alt="Signature" className="max-h-20" />
                      </div>
                    </div>
                  )}

                  <div className="mb-6">
                    <p className="text-xs font-medium text-slate-500 mb-1">Typed Signature</p>
                    <p className="text-lg font-serif italic text-slate-800">{detail.typed_signature}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mb-4">
                    <a
                      href={`${API}/agreements/${detail.id}/pdf`}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#235347] text-white rounded-xl text-sm font-medium hover:bg-[#1A3F36]"
                      data-testid="admin-download-pdf"
                    >
                      <DownloadSimple size={16} /> Download PDF
                    </a>
                    <button
                      onClick={() => { setShowResend(true); setResendEmail(detail.signer_email); }}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50"
                      data-testid="admin-resend-btn"
                    >
                      <EnvelopeSimple size={16} /> Resend
                    </button>
                  </div>

                  {showResend && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <p className="text-xs text-slate-500 mb-2">Resend signed copy to:</p>
                      <input
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none mb-2"
                        data-testid="resend-email-input"
                      />
                      <button
                        onClick={resendCopy}
                        disabled={resending}
                        className="w-full bg-[#235347] text-white rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50"
                        data-testid="resend-confirm-btn"
                      >
                        {resending ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between items-start border-b border-slate-100 pb-2">
    <span className="text-xs text-slate-500 flex-shrink-0">{label}</span>
    <span className={`text-sm text-slate-700 text-right ml-4 ${mono ? 'font-mono text-xs break-all' : ''}`}>
      {value}
    </span>
  </div>
);

export default AdminNDARegister;
